import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { useAuthModal } from './AuthModalContext';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';

interface ProtectedRouteProps {
  children: ReactNode;
  requireSignIn?: boolean;
  requireData?: boolean;
  requireDataEntry?: boolean;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({
  children,
  requireSignIn = false,
  requireData = false,
  requireDataEntry = false,
  requireAdmin = false,
}: ProtectedRouteProps) {
  const { isLoaded: authLoaded } = useAuth();
  const { isLoading, isAuthenticated, canViewData, canEnterData, canAccessAdmin } = useAccessLevel();
  const { openAuthModal } = useAuthModal();

  const needsAuth = requireSignIn || requireData || requireDataEntry || requireAdmin;
  const allowed = requireAdmin
    ? canAccessAdmin
    : requireDataEntry
      ? canEnterData
      : requireData
        ? canViewData
        : requireSignIn
          ? isAuthenticated
          : true;

  useEffect(() => {
    if (!authLoaded || isLoading) return;
    if (!isAuthenticated && needsAuth) {
      openAuthModal('sign-in');
    }
  }, [authLoaded, isLoading, isAuthenticated, needsAuth, openAuthModal]);

  if (!authLoaded || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner size={60} />
      </div>
    );
  }

  if (!allowed) {
    if (!isAuthenticated) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign in required</h1>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Create an account or sign in to open this page.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button type="button" onClick={() => openAuthModal('sign-in')} className="min-h-[44px]">
                Sign in
              </Button>
              <Link
                to="/"
                className="inline-flex items-center justify-center min-h-[44px] px-6 py-3 rounded-md font-medium border-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors duration-150 focus-ring"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access denied</h1>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {requireDataEntry
              ? 'Data entry is available to designated Special Access users and ORBCoN administrators.'
              : requireData
                ? 'Data is available to Ontario Healthcare Professionals with a verified hospital email, designated Special Access users, and ORBCoN administrators.'
                : 'You do not have permission to view this page.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center min-h-[44px] px-6 py-3 rounded-md font-medium bg-primary text-white hover:bg-primary-dark transition-colors duration-150 focus-ring"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
